/**
 * Work Log Review fixture — task 159c.
 *
 * One snapshot of the 159a response shape used in mockup mode (and as the
 * input to the smoke test). Dates derive from `FROZEN_NOW` (2026-04-13);
 * the window is the seven days ending on FROZEN_NOW, classificationSource
 * defaulted to ['fallback'], no group-by, sorted by hours DESC.
 *
 * The fixture deliberately mixes:
 *   - Two employees (Sarah Chen, Marcus Lee) so the `employee` group-by
 *     and the `distinctEmployees` count have something to bite on.
 *   - Five distinct apps (chrome, Outlook, Slack, Teams, code) so the
 *     `app` group-by yields five group rows.
 *   - Mostly fallback classifications with a single `rule`-classified
 *     event so the classificationSource filter shape is realistic.
 *   - One event with a non-null `project` (the `rule`-classified one) and
 *     all others with `project = null` (falling into Internal/Not Set as
 *     fallback always does).
 *   - PII-shape data (titles, urls) is invented per CLAUDE.md.
 *
 * The companion `WORK_LOG_REVIEW_CAP_HIT_FIXTURE` flips `groupCapHit=true`
 * and trims `groups` to the cap so the smoke + a11y tests can render the
 * "narrow your filters" hint.
 */

import { FROZEN_NOW_MS } from '@/lib/frozenTime';
import type {
  WorkLogReviewEventRow,
  WorkLogReviewGroupRow,
  WorkLogReviewResponse,
} from '@/types/workLogReview';

/**
 * Default group-row cap for the fixture-mode hook + the page's cap-hit
 * banner. Mirrors the `WORK_LOG_REVIEW_GROUP_CAP` tier-2 config key on
 * the backend (parent task 159 locked decision #5). Live mode reads the
 * cap off the response (`groupCap`); the fixture/page constant exists so
 * the banner copy and the fixture-mode `groupCap` field never drift.
 */
export const WORK_LOG_REVIEW_GROUP_CAP_DEFAULT = 500;

const DAY_MS = 24 * 60 * 60 * 1000;

function isoDayUTC(offsetDays: number): string {
  return new Date(FROZEN_NOW_MS - offsetDays * DAY_MS).toISOString();
}

function isoEventTime(offsetDays: number, hourOfDay: number): string {
  // Build a per-hour timestamp anchored on FROZEN_NOW's day at 00:00 UTC.
  const dayStart = new Date(FROZEN_NOW_MS - offsetDays * DAY_MS);
  dayStart.setUTCHours(hourOfDay, 0, 0, 0);
  return dayStart.toISOString();
}

const FROM_ISO = isoDayUTC(6);
const TO_ISO = isoDayUTC(0);

// Employees referenced by id only — these match Meridian's roster in
// `employees.ts`. The fixture does not need to import that file (the
// hook short-circuits to the response shape, not the entity layer).
const EMP_SARAH = { id: 'emp-sarah-chen', name: 'Sarah Chen' };
const EMP_MARCUS = { id: 'emp-marcus-lee', name: 'Marcus Lee' };

function makeEvent(
  i: number,
  e: { id: string; name: string },
  partial: Partial<WorkLogReviewEventRow> & {
    occurredAt: string;
    app: string | null;
  },
): WorkLogReviewEventRow {
  return {
    id: `evt-${i.toString().padStart(4, '0')}`,
    occurredAt: partial.occurredAt,
    employeeId: e.id,
    employeeName: e.name,
    app: partial.app,
    appFileName: partial.appFileName ?? null,
    title: partial.title ?? null,
    url: partial.url ?? null,
    site: partial.site ?? null,
    rootDomain: partial.rootDomain ?? null,
    project: partial.project ?? null,
    classificationSource: partial.classificationSource ?? 'fallback',
    classifiedByRuleId: partial.classifiedByRuleId ?? null,
    billable: partial.billable ?? false,
  };
}

const EVENTS: WorkLogReviewEventRow[] = [
  // Day 0 (most recent)
  makeEvent(1, EMP_SARAH, {
    occurredAt: isoEventTime(0, 9),
    app: 'chrome.exe',
    appFileName: 'chrome.exe',
    title: 'Inbox — Gmail',
    url: 'https://mail.google.com/mail/u/0/#inbox',
    site: 'mail.google.com',
    rootDomain: 'google.com',
  }),
  makeEvent(2, EMP_SARAH, {
    occurredAt: isoEventTime(0, 10),
    app: 'Outlook.exe',
    appFileName: 'Outlook.exe',
    title: 'Re: Q2 budget review',
  }),
  makeEvent(3, EMP_MARCUS, {
    occurredAt: isoEventTime(0, 11),
    app: 'Slack.exe',
    appFileName: 'Slack.exe',
    title: '#general — Meridian',
  }),
  makeEvent(4, EMP_MARCUS, {
    occurredAt: isoEventTime(0, 14),
    app: 'Code.exe',
    appFileName: 'Code.exe',
    title: 'src/dashboard.ts — meridian',
  }),
  // Day 1
  makeEvent(5, EMP_SARAH, {
    occurredAt: isoEventTime(1, 9),
    app: 'chrome.exe',
    title: 'Calendar — Today',
    url: 'https://calendar.google.com/calendar/u/0/r',
    site: 'calendar.google.com',
    rootDomain: 'google.com',
  }),
  makeEvent(6, EMP_SARAH, {
    occurredAt: isoEventTime(1, 10),
    app: 'Teams.exe',
    appFileName: 'Teams.exe',
    title: 'Standup — Engineering',
  }),
  makeEvent(7, EMP_MARCUS, {
    occurredAt: isoEventTime(1, 13),
    app: 'chrome.exe',
    title: 'Pull request #4218',
    url: 'https://github.com/meridian/app/pull/4218',
    site: 'github.com',
    rootDomain: 'github.com',
  }),
  // Day 2 — one rule-classified event for source-filter realism
  makeEvent(8, EMP_SARAH, {
    occurredAt: isoEventTime(2, 9),
    app: 'Outlook.exe',
    title: 'Meridian Capital — invoice draft',
    project: {
      id: 'prj-meridian-mna',
      name: 'M&A Advisory',
      clientId: 'cli-meridian-capital',
      clientName: 'Meridian Capital',
    },
    classificationSource: 'rule',
    classifiedByRuleId: 'rule-outlook-meridian',
    billable: true,
  }),
  makeEvent(9, EMP_MARCUS, {
    occurredAt: isoEventTime(2, 14),
    app: 'Code.exe',
    title: 'app.tsx — meridian dashboard',
  }),
  // Day 3
  makeEvent(10, EMP_SARAH, {
    occurredAt: isoEventTime(3, 11),
    app: 'chrome.exe',
    title: 'AWS console — EC2',
    url: 'https://us-east-1.console.aws.amazon.com/ec2/home',
    site: 'us-east-1.console.aws.amazon.com',
    rootDomain: 'amazon.com',
  }),
  makeEvent(11, EMP_MARCUS, {
    occurredAt: isoEventTime(3, 15),
    app: 'Slack.exe',
    title: '#meridian-eng — DM thread',
  }),
  // Day 4
  makeEvent(12, EMP_SARAH, {
    occurredAt: isoEventTime(4, 9),
    app: 'chrome.exe',
    title: 'Stack Overflow — typescript zod',
    url: 'https://stackoverflow.com/questions/tagged/zod',
    site: 'stackoverflow.com',
    rootDomain: 'stackoverflow.com',
  }),
  // Day 5
  makeEvent(13, EMP_MARCUS, {
    occurredAt: isoEventTime(5, 13),
    app: 'Teams.exe',
    title: 'All-hands — quarterly',
  }),
  // Day 6 (oldest in window)
  makeEvent(14, EMP_SARAH, {
    occurredAt: isoEventTime(6, 10),
    app: 'Outlook.exe',
    title: 'Meeting prep — board update',
  }),
];

const TOTAL_EVENT_COUNT = EVENTS.length; // 14
const TOTAL_HOURS = +(TOTAL_EVENT_COUNT * (5 / 60)).toFixed(2); // 14 * (5min/screenshot) ≈ 1.17h
const DISTINCT_EMPLOYEES = 2;

// Default response: groupBy=[] → one synthetic "all" group with the totals.
// 159a returns a single empty-key group for the empty-groupBy case (the
// route always builds a group map; with no dims, every event hashes to
// the same bucket).
const DEFAULT_GROUPS: WorkLogReviewGroupRow[] = [
  {
    key: {},
    count: TOTAL_EVENT_COUNT,
    hours: TOTAL_HOURS,
    distinctEmployees: DISTINCT_EMPLOYEES,
    firstSeenAt: EVENTS[EVENTS.length - 1].occurredAt,
    lastSeenAt: EVENTS[0].occurredAt,
    percentOfFiltered: 100,
  },
];

export const WORK_LOG_REVIEW_FIXTURE: WorkLogReviewResponse = {
  filter: {
    from: FROM_ISO,
    to: TO_ISO,
    classificationSource: ['fallback'],
    groupBy: [],
    sortBy: 'hours',
    sortDir: 'desc',
  },
  totals: {
    eventCount: TOTAL_EVENT_COUNT,
    hours: TOTAL_HOURS,
    distinctEmployees: DISTINCT_EMPLOYEES,
    distinctGroups: 1,
  },
  groups: DEFAULT_GROUPS,
  groupCapHit: false,
  groupCap: WORK_LOG_REVIEW_GROUP_CAP_DEFAULT,
  events: {
    page: 1,
    pageSize: 100,
    totalCount: TOTAL_EVENT_COUNT,
    rows: EVENTS,
  },
};

/**
 * Cap-hit variant — same events, but the response advertises 600 distinct
 * groups with `groupCapHit=true`. The smoke test toggles the hook to this
 * variant via a query-param flag (`__capHit=1`) so the "narrow your filters"
 * hint can render in fixture mode without real data scaling.
 */
export const WORK_LOG_REVIEW_CAP_HIT_FIXTURE: WorkLogReviewResponse = {
  ...WORK_LOG_REVIEW_FIXTURE,
  filter: {
    ...WORK_LOG_REVIEW_FIXTURE.filter,
    groupBy: ['title'],
  },
  totals: {
    ...WORK_LOG_REVIEW_FIXTURE.totals,
    distinctGroups: 612,
  },
  groups: WORK_LOG_REVIEW_FIXTURE.events.rows.map((e): WorkLogReviewGroupRow => {
    return {
      key: { title: e.title ?? null },
      count: 1,
      hours: +(1 * (5 / 60)).toFixed(2),
      distinctEmployees: 1,
      firstSeenAt: e.occurredAt,
      lastSeenAt: e.occurredAt,
      percentOfFiltered: +((1 / TOTAL_EVENT_COUNT) * 100).toFixed(2),
    };
  }),
  groupCapHit: true,
};
